import { refreshToken as refreshTokenApi } from '@modusign/openapi-client-user';
import { jwtDecode, JwtPayload } from 'jwt-decode';

import { isJWTToken } from '@app/libs/auth/isJWTToken';
import { isTokenExpired } from '@app/libs/auth/isTokenExpired';
import { tokenUserId } from '@app/libs/auth/tokenUserId';
import { logger } from '@app/libs/logger';
import Observable from '@app/libs/Observable';

interface ModusignJwtPayload extends JwtPayload {
  /**
   * TODO(@taehwanno): urlPaths는 백엔드 구현상 필수값이 맞지만 특정 상황에서 전달되지 않고 있으므로 optional로 변경
   * @see https://modusign.slack.com/archives/C0499KFM9R9/p1701329757629809
   */
  urlPaths?: string[];
}

export type Tokens = { accessToken: string; refreshToken: string };

class AuthService {
  private observable = new Observable();

  private storage: Storage | undefined;

  private ID_TOKEN_KEY = 'id_token';

  /** refresh token을 위한 storage key입니다. 브라우저에서 key를 통해 의미를 쉽게 파악하지 못하도록 하기 위함입니다. */
  private ID_TOKEN_SECOND_KEY = 'id_token_second';

  constructor() {
    window.addEventListener(
      'storage',
      (e) => {
        if (e.key === this.ID_TOKEN_KEY || e.key === this.ID_TOKEN_SECOND_KEY) {
          /** token이 새로 추가되거나, 제거되거나, tokenUserId가 바뀌었을 경우에만 notify합니다. */
          if (!e.oldValue || !e.newValue || tokenUserId(e.oldValue) !== tokenUserId(e.newValue)) {
            this.observable.notify();
          }
        }
      },
      false
    );
  }

  subscribe = (fn: () => void): (() => void) => {
    return this.observable.subscribe(fn);
  };

  setStorage = (newStorage: Storage) => {
    this.storage = newStorage;
  };

  getAccessToken = (): string | null => {
    if (!this.storage) {
      throw new Error('Set auth service storage first.');
    }

    return this.storage.getItem(this.ID_TOKEN_KEY);
  };

  getRefreshToken = (): string | null => {
    if (!this.storage) {
      throw new Error('Set auth service storage first.');
    }

    return this.storage.getItem(this.ID_TOKEN_SECOND_KEY);
  };

  hasAccessToken = (): boolean => {
    return !!this.getAccessToken();
  };

  hasRefreshToken = (): boolean => {
    return !!this.getRefreshToken();
  };

  getTokens = (): Tokens => {
    return { accessToken: this.getAccessToken() ?? '', refreshToken: this.getRefreshToken() ?? '' };
  };

  setTokens = (tokens: Tokens): void => {
    if (!this.storage) {
      throw new Error('Set auth service storage first.');
    }
    this.storage.setItem(this.ID_TOKEN_KEY, tokens.accessToken || '');
    this.storage.setItem(this.ID_TOKEN_SECOND_KEY, tokens.refreshToken || '');
  };

  removeTokens = (): void => {
    if (!this.storage) {
      throw new Error('Set auth service storage first.');
    }

    if (this.storage.getItem(this.ID_TOKEN_KEY) || this.storage.getItem(this.ID_TOKEN_SECOND_KEY)) {
      this.storage.removeItem(this.ID_TOKEN_KEY);
      this.storage.removeItem(this.ID_TOKEN_SECOND_KEY);
      this.observable.notify();
    }
  };

  isAccessTokenExpired = (): boolean => {
    const token = this.getAccessToken();
    if (!token || !isJWTToken(token)) {
      return true;
    }
    return isTokenExpired(token);
  };

  isRefreshTokenExpired = (): boolean => {
    const token = this.getRefreshToken();
    if (!token || !isJWTToken(token)) {
      return true;
    }
    return isTokenExpired(token);
  };

  /** accessToken이 만료되었을 경우에만, refreshToken으로 재발급 받습니다. */
  refreshTokens = async (): Promise<void> => {
    if (!this.isAccessTokenExpired()) {
      return;
    }
    try {
      if (this.isRefreshTokenExpired()) {
        throw new Error('all tokens are expired');
      }
      const newTokens = await refreshTokenApi(this.getTokens(), { credentials: false });
      this.setTokens(newTokens);
    } catch (error) {
      this.removeTokens();
      logger.error(`Request refreshing accessToken failed with ${error.message}`, error);
      throw new Error(error);
    }
  };

  getUserUrlPaths = (): string[] => {
    const token = this.getAccessToken();
    if (!token) {
      return [];
    }
    try {
      const decoded = jwtDecode<ModusignJwtPayload>(token);
      if (!decoded.urlPaths) {
        /**
         * TODO(@taehwanno): 오류지원 해결이 완료됐다면 제거 필요
         * @see https://modusign.slack.com/archives/C0499KFM9R9/p1701334863609949?thread_ts=1701329757.629809&cid=C0499KFM9R9
         */
        logger.info('urlPaths is not defined in jwt token');
      }
      return decoded.urlPaths ?? [];
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error(error);
      }
      return [];
    }
  };

  getSub = (): string | null => {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }
    try {
      return tokenUserId(token);
    } catch (error) {
      logger.error(error);
      return null;
    }
  };
}

// AuthService constructor에서 event handler를 등록합니다. 만약 singleton 구현 방식 수정시 메모리 누수가 일어나지 않도록 고려해야합니다.
const authService = new AuthService();
export default authService;
